// src/stores/useUserLayersStore.js
//
// Manages user-created (drawn) and user-imported layers for the active project.
//
// Lifecycle:
//   1. MapPage calls loadUserLayers(projectId) on bootstrap
//   2. Layers register into useLayerRegistryStore with scope: "user"
//   3. Share toggle calls shareLayer() / unshareLayer() → PATCH API → re-register
//   4. saveDrawnLayer() called from AoiToolsMap after a draw completes
//   5. importLayer() called from file drop handler
//
// Registry integration:
//   Each layer is registered as a GeoJSON source + fill/line layers in
//   useLayerRegistryStore with scope: "user". The registry handles visibility
//   toggling. This store owns persistence (API) and metadata (name, visibility).

import { create } from "zustand";
import { fetchWithAuth } from "../api/fetchWithAuth";
import { useLayerRegistryStore } from "./useLayerRegistryStore";
import { useProjectStore } from "./useProjectStore";

// ─────────────────────────────────────────────────────────────────────────────
// HELPERS
// ─────────────────────────────────────────────────────────────────────────────

// Derive a color for the layer based on its type
function layerColor(layerType) {
  switch (layerType) {
    case "drawn": return "#16f2d4"; // accent-teal
    case "imported": return "#1f9dff"; // accent-blue
    case "ai_result": return "#f97316"; // orange
    default: return "#94a3b8"; // slate
  }
}

// Build registry layer descriptors from a project_layers DB row.
//
// One DB layer → ONE "user-layer" registry entry (the `-fill` primary handle).
// useSyncRegistryLayers.syncGeoJsonLayer fans that single entry out into the
// actual MapLibre sublayers by geometry type from the SAME source:
//   -fill   (Polygon)              -stroke (Polygon + LineString outlines)
//   -point  (Point → circle)       -note-* (drawn-note symbols)
// so points, lines and polygons all render. Visibility toggling the entry
// propagates to every suffix (the dispatch loops the `-fill/-line/-stroke/
// -point/-note-*` ids), and deleteLayer's prefix teardown removes them all.
// Per-feature vs static color (`colorExpr` / `usePerFeature`) is exposed via
// `_fillColor/_strokeColor/_pointColor/_staticColor` for syncGeoJsonLayer.
function buildRegistryLayers(dbLayer) {
  const { id, name, geojson, style, layer_type, visibility, owner_id } = dbLayer;
 
  const sourceId    = `user-layer-${id}`;
  const staticColor = style?.color ?? layerColor(layer_type);
 
  // Detect per-feature color — true for exported annotation layers re-imported,
  // false for plain GeoJSON/shapefiles that have no color property.
  const features       = geojson?.features ?? [];
  const hasFeatureColor = features.some(f => f?.properties?.color);
  const usePerFeature  = layer_type === "drawn" || hasFeatureColor;
 
  const colorExpr = ["coalesce", ["get", "color"], staticColor];
 
  const fillColor   = usePerFeature ? colorExpr : (style?.fillColor  ?? staticColor);
  const strokeColor = usePerFeature ? colorExpr : (style?.lineColor   ?? staticColor);
  const pointColor  = usePerFeature ? colorExpr : staticColor;
 
  return [
    {
      id: `${sourceId}-fill`,
      name,
      scope: "user",
      kind: "user-layer",
      visible: false,
      default_visible: false,
      layer_type,
      visibility,
      owner_id,
      db_layer_id: id,
      _fillColor:   fillColor,
      _strokeColor: strokeColor,
      _pointColor:  pointColor,
      _staticColor: staticColor,
      source: { type: "geojson-inline", data: geojson, sourceId },
      maplibre_layer: {
        id: `${sourceId}-fill`,
        type: "fill",
        source: sourceId,
        filter: ["==", ["geometry-type"], "Polygon"],
        layout: { visibility: "visible" },
        paint: {
          "fill-color": fillColor,
          "fill-opacity": style?.fillOpacity ?? 0.15,
        },
      },
    },
  ];
}

// setLayerColor's per-layer debounce timers, and the style each layer had
// before the current burst of changes (what a failed save rolls back to).
// An entry lives only until that burst's save settles, so a later burst
// starts from the row as it is then, not from a remembered value.
export const COLOR_SAVE_DEBOUNCE_MS = 400;
const colorTimers = new Map();
const savedStyles = new Map();

// Put `style` on a layer's store rows and on its existing registry entry,
// leaving the entry's `visible` and source data exactly as they are.
function applyLayerStyle(set, row, style) {
  const withStyle = (l) => (l.id === row.id ? { ...l, style } : l);
  set(s => ({ layers: s.layers.map(withStyle), shared: s.shared.map(withStyle) }));

  const [fresh] = buildRegistryLayers({ ...row, style });
  const registry = useLayerRegistryStore.getState();
  const existing = registry.layers[fresh.id];
  if (!existing) return;
  registry.registerLayers([{
    ...existing,
    _fillColor:     fresh._fillColor,
    _strokeColor:   fresh._strokeColor,
    _pointColor:    fresh._pointColor,
    _staticColor:   fresh._staticColor,
    maplibre_layer: fresh.maplibre_layer,
  }]);
}

// ─────────────────────────────────────────────────────────────────────────────
// STORE
// ─────────────────────────────────────────────────────────────────────────────

export const useUserLayersStore = create((set, get) => ({

  // ── State ──────────────────────────────────────────────────────────────────
  layers: [],          // raw DB rows for the current user
  shared: [],          // layers shared with the project (includes own + others')
  loading: false,
  importing: false,    // true while importLayer() is in-flight
  error: null,

  // ── Load ───────────────────────────────────────────────────────────────────

  loadUserLayers: async (projectId) => {
    if (!projectId || projectId === "global") return;

    // Clear immediately — prevents stale layers from previous project
    // flashing in the panel while the new fetch is in flight.
    set({ loading: true, error: null, layers: [], shared: [] });

    try {
      const [ownRes, sharedRes] = await Promise.all([
        fetchWithAuth(`/api/v1/${projectId}/layers/user`),
        fetchWithAuth(`/api/v1/${projectId}/layers/shared`),
      ]);

      if (!ownRes.ok || !sharedRes.ok) throw new Error("Failed to load layers");

      const [own, shared] = await Promise.all([ownRes.json(), sharedRes.json()]);

      set({ layers: own, shared, loading: false });

      // Register all visible layers into the registry
      // Own layers: always register (user controls visibility locally)
      // Shared layers from others: register with scope:"user", scope note "shared"
      // Register own layers + shared layers into the registry
      const allToRegister = [
        ...own.flatMap(dl => buildRegistryLayers(dl)),
        ...shared.flatMap(dl => buildRegistryLayers(dl)),
      ];

      useLayerRegistryStore.getState().registerLayers(allToRegister, {
        replaceScope: "user",
      });

    } catch (err) {
      console.error("[useUserLayersStore] load failed", err);
      set({ loading: false, error: err.message });
    }
  },

  saveAnnotationLayer: async ({ projectId, name, geojson, color }) => {

    console.log("[saveAnnotationLayer]", JSON.stringify(geojson, null, 2));

    const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        name,
        layer_type: "drawn",
        geojson,
        style: { color },
      }),
    });

    if (!res.ok) throw new Error("Failed to save annotation layer");
    const created = await res.json();

    console.log("[saveAnnotationLayer] created", created.id,
      "features:", created.geojson?.features?.length ?? "NO GEOJSON");

    // Add to local store lists without reloading everything
    set(s => ({
      layers: [created, ...s.layers],
      shared: [created, ...s.shared],
    }));

    // Register only the new layer — no replaceScope, no wipe, no imagery reload
    const registryLayers = buildRegistryLayers(created);
    useLayerRegistryStore.getState().registerLayers(registryLayers);

    const fillId = `user-layer-${created.id}-fill`;
    const regLayer = useLayerRegistryStore.getState().layers[fillId];
    console.log("[register check] features:",
      regLayer?.source?.data?.features?.length ?? "no source data");

    // Auto-show it immediately so the user sees their work on the map
    useLayerRegistryStore.getState().setLayerVisibilityById(fillId, true);

    return created;
  },

  // ── Save a drawn layer ─────────────────────────────────────────────────────

  saveDrawnLayer: async ({ name, geojson, style } = {}) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return null;
    if (!geojson) return null;

    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name || "Drawn layer", layer_type: "drawn", geojson, style }),
      });

      if (!res.ok) throw new Error(await res.text());
      const created = await res.json();

      // Add to local state
      set(s => ({ layers: [created, ...s.layers], shared: [created, ...s.shared] }));

      // Register in the map registry
      const registryLayers = buildRegistryLayers(created);
      useLayerRegistryStore.getState().registerLayers(registryLayers);

      return created;
    } catch (err) {
      console.error("[useUserLayersStore] saveDrawnLayer failed", err);
      return null;
    }
  },

  // ── Import a file ──────────────────────────────────────────────────────────

  importLayer: async (file, name) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return null;

    const formData = new FormData();
    formData.append("file", file);
    if (name) formData.append("name", name);

    set({ importing: true });
    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user/upload`, {
        method: "POST",
        body: formData,
        // Don't set Content-Type — browser sets multipart boundary automatically
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: "Upload failed" }));
        throw new Error(err.error ?? "Upload failed");
      }

      const created = await res.json();

      // If the upload response omits geojson (common for multipart endpoints),
      // fetch the full record so buildRegistryLayers has source data.
      let fullLayer = created;
      if (!created.geojson) {
        try {
          const detailRes = await fetchWithAuth(
            `/api/v1/${projectId}/layers/user/${created.id}`
          );
          if (detailRes.ok) fullLayer = await detailRes.json();
        } catch (_) {
          // If fetch fails, proceed with what we have — layer will show on reload
        }
      }

      set(s => ({
        importing: false,
        layers: [fullLayer, ...s.layers],
        shared: [fullLayer, ...s.shared],
      }));

      const registryLayers = buildRegistryLayers(fullLayer);
      useLayerRegistryStore.getState().registerLayers(registryLayers);

      // Auto-show the imported layer
      const fillId = `user-layer-${fullLayer.id}-fill`;
      useLayerRegistryStore.getState().setLayerVisibilityById(fillId, true);

      return fullLayer;

    } catch (err) {
      set({ importing: false });
      console.error("[useUserLayersStore] importLayer failed", err);
      throw err; // re-throw so UI can show error
    }
  },

  // ── Three-phase import (inspect → select → import) ───────────────────────────
  //
  // Phase 1: probe sublayers without converting or writing. Returns
  // { upload_token, sublayers }. The caller decides whether to show the
  // QGIS-style picker (sublayers > 1) or import directly (single sublayer).

  inspectLayer: async (file) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return null;

    const formData = new FormData();
    formData.append("file", file);

    const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user/inspect`, {
      method: "POST",
      body: formData,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Inspect failed" }));
      throw new Error(err.error ?? "Inspect failed");
    }
    return res.json(); // { upload_token, sublayers: [...] }
  },

  // Phase 3: convert + persist the selected sublayers. Registers + auto-shows
  // each created layer. Returns { created, failed } so the caller can toast
  // partial failures (a layer that fails to convert does not sink the batch).
  importSelectedLayers: async (uploadToken, selections) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || projectId === "global") return { created: [], failed: [] };

    set({ importing: true });
    try {
      const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user/import`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ upload_token: uploadToken, selections }),
      });

      const body = await res.json().catch((e) => { console.error("[ui/src/stores/useUserLayersStore.js] res.json() failed (swallowed):", e); return {}; });
      if (!res.ok) {
        // All-failed comes back 422 with failed[] — surface it instead of throwing
        // so the UI can report which layers failed rather than a generic error.
        if (Array.isArray(body.failed) && body.failed.length) {
          set({ importing: false });
          return { created: [], failed: body.failed };
        }
        throw new Error(body.error ?? "Import failed");
      }

      const created = body.created ?? [];
      const failed = body.failed ?? [];

      set(s => ({
        importing: false,
        layers: [...created, ...s.layers],
        shared: [...created, ...s.shared],
      }));

      const registry = useLayerRegistryStore.getState();
      for (const layer of created) {
        registry.registerLayers(buildRegistryLayers(layer));
        // Auto-show via the `-fill` handle — the sync dispatch propagates
        // visibility to every geometry sublayer (incl. point-only layers).
        registry.setLayerVisibilityById(`user-layer-${layer.id}-fill`, true);
      }

      return { created, failed };
    } catch (err) {
      set({ importing: false });
      console.error("[useUserLayersStore] importSelectedLayers failed", err);
      throw err;
    }
  },

  // ── Share / Unshare ────────────────────────────────────────────────────────

  shareLayer: async (dbLayerId) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;

    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layers/user/${dbLayerId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visibility: "project" }),
        }
      );

      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();

      set(s => ({
        layers: s.layers.map(l => l.id === dbLayerId ? updated : l),
        shared: s.shared.map(l => l.id === dbLayerId ? updated : l),
      }));

      // Re-register with updated metadata
      const registryLayers = buildRegistryLayers(updated);
      useLayerRegistryStore.getState().registerLayers(registryLayers);

    } catch (err) {
      console.error("[useUserLayersStore] shareLayer failed", err);
    }
  },

  unshareLayer: async (dbLayerId) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;

    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layers/user/${dbLayerId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ visibility: "private" }),
        }
      );

      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();

      set(s => ({
        layers: s.layers.map(l => l.id === dbLayerId ? updated : l),
        shared: s.shared.map(l => l.id === dbLayerId ? updated : l),
      }));

      const registryLayers = buildRegistryLayers(updated);
      useLayerRegistryStore.getState().registerLayers(registryLayers);

    } catch (err) {
      console.error("[useUserLayersStore] unshareLayer failed", err);
    }
  },

  // ── Rename ─────────────────────────────────────────────────────────────────

  renameLayer: async (dbLayerId, name) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;

    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layers/user/${dbLayerId}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ name }),
        }
      );

      if (!res.ok) throw new Error(await res.text());
      const updated = await res.json();

      set(s => ({
        layers: s.layers.map(l => l.id === dbLayerId ? updated : l),
        shared: s.shared.map(l => l.id === dbLayerId ? updated : l),
      }));
    } catch (err) {
      console.error("[useUserLayersStore] renameLayer failed", err);
    }
  },

  // ── Recolor ────────────────────────────────────────────────────────────────
  //
  // The My Layers swatch. A native color picker fires on every movement while
  // the user drags, so the map follows each change right away and the PATCH
  // is debounced per layer -- one save when the user settles, not dozens.
  //
  // The registry entry is PATCHED, not rebuilt. Re-registering the output of
  // buildRegistryLayers would reset `visible` to its hardcoded false (hiding
  // the layer the user is recoloring) and hand the map a new FeatureCollection
  // reference, which re-uploads the whole source for a paint change.
  // useSyncRegistryLayers' syncUserLayerPaint applies the new colors.
  //
  // If the save fails the color goes back to the last one the server accepted,
  // unless a newer change is already waiting to be saved.

  setLayerColor: (dbLayerId, color) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;

    const row = [...get().layers, ...get().shared].find(l => l.id === dbLayerId);
    if (!row) return;

    if (!savedStyles.has(dbLayerId)) savedStyles.set(dbLayerId, row.style ?? null);
    applyLayerStyle(set, row, { ...(row.style ?? {}), color });

    clearTimeout(colorTimers.get(dbLayerId));
    colorTimers.set(dbLayerId, setTimeout(async () => {
      colorTimers.delete(dbLayerId);
      try {
        const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user/${dbLayerId}`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ style: { color } }),
        });
        if (!res.ok) throw new Error(await res.text());
        // Saved: what the store holds is now what the server holds.
        if (!colorTimers.has(dbLayerId)) savedStyles.delete(dbLayerId);
      } catch (err) {
        console.error("[useUserLayersStore] setLayerColor failed", err);
        if (colorTimers.has(dbLayerId)) return; // a newer color is on its way
        const current = [...get().layers, ...get().shared].find(l => l.id === dbLayerId);
        if (current) applyLayerStyle(set, current, savedStyles.get(dbLayerId));
        savedStyles.delete(dbLayerId);
      }
    }, COLOR_SAVE_DEBOUNCE_MS));
  },

  // ── Update a drawn layer in place ──────────────────────────────────────────
  //
  // The counterpart to saveAnnotationLayer, for a layer being re-edited rather
  // than created. PATCHes name + geojson + style so the row keeps its id, and
  // with it its sharing state, created_at and any references elsewhere.
  //
  // Re-registering afterwards is the part that is easy to miss: the registry
  // holds the OLD FeatureCollection inline, so without this the request
  // succeeds, the database is correct, and the map keeps drawing the previous
  // geometry until a reload. Unlike the create path this does not force
  // visibility on — the user is editing a layer they were already looking at,
  // and flipping it on would override a deliberate hide.
  updateAnnotationLayer: async ({ layerId, name, geojson, color }) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId || !layerId) return null;

    const res = await fetchWithAuth(`/api/v1/${projectId}/layers/user/${layerId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, geojson, style: { color } }),
    });

    if (!res.ok) throw new Error("Failed to update annotation layer");
    const updated = await res.json();

    set(s => ({
      layers: s.layers.map(l => (l.id === layerId ? updated : l)),
      shared: s.shared.map(l => (l.id === layerId ? updated : l)),
    }));

    useLayerRegistryStore.getState().registerLayers(buildRegistryLayers(updated));

    return updated;
  },

  // ── Delete ─────────────────────────────────────────────────────────────────

  deleteLayer: async (dbLayerId) => {
    const projectId = useProjectStore.getState().activeProjectId;
    if (!projectId) return;

    const prefix = `user-layer-${dbLayerId}`;
    const registry = useLayerRegistryStore.getState().layers;
    const toRemove = Object.keys(registry).filter(k => k.startsWith(prefix));

    // Optimistically hide on the map immediately — setLayerVisibilityById
    // triggers the useSyncRegistryLayers effect which calls setLayoutProperty.
    // For geojson-inline layers whose MapLibre layer already exists this is
    // instant; no sources are rebuilt.
    toRemove.forEach(id => {
      useLayerRegistryStore.getState().setLayerVisibilityById(id, false);
    });

    try {
      const res = await fetchWithAuth(
        `/api/v1/${projectId}/layers/user/${dbLayerId}`,
        { method: "DELETE" }
      );

      if (!res.ok) throw new Error(await res.text());

      // Remove from local store lists
      set(s => ({
        layers: s.layers.filter(l => l.id !== dbLayerId),
        shared: s.shared.filter(l => l.id !== dbLayerId),
      }));

      // Remove registry entries entirely by re-registering without them
      useLayerRegistryStore.getState().registerLayers(
        toRemove.map(id => ({ ...registry[id], scope: "deleted", visible: false }))
      );

    } catch (err) {
      // Rollback optimistic hide
      toRemove.forEach(id => {
        useLayerRegistryStore.getState().setLayerVisibilityById(id, registry[id]?.visible ?? false);
      });
      console.error("[useUserLayersStore] deleteLayer failed", err);
    }
  },

  // ── Reset on project change ────────────────────────────────────────────────

  reset: () => set({ layers: [], shared: [], loading: false, importing: false, error: null }),
}));
